/**
 * Modal showing all scrape jobs for one patient — both active and recent
 * completions. Opened from the per-row indicator on the TreatmentPlans
 * list. Uses a card-based timeline layout: each job is its own readable
 * row with a colored side-rail, a status icon, the operation + carrier,
 * relative timestamp, and (when applicable) the carrier's error message.
 *
 * react-redux 6.x — uses connect().
 */

import React from "react";
import { connect } from "react-redux";
import { Modal } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  KeyOutlined,
  LoadingOutlined,
  StopOutlined,
  CloudSyncOutlined,
} from "@ant-design/icons";

import "./ScrapeJobsIndicator.css";

const ACTIVE_STATES = new Set(["queued", "claimed", "running", "awaiting_2fa"]);
const TERMINAL_STATES = new Set(["succeeded", "failed", "cancelled", "timed_out"]);

function jobLabel(job) {
  const op = (job.operation_type || "").replace(/_/g, " ");
  if (!op) return "Insurance fetch";
  return op.charAt(0).toUpperCase() + op.slice(1);
}

function carrierName(job) {
  if (!job || !job.carrier_code) return "";
  const c = String(job.carrier_code).toLowerCase();
  return c.charAt(0).toUpperCase() + c.slice(1);
}

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return diffSec + "s ago";
  if (diffSec < 3600) return Math.floor(diffSec / 60) + "m ago";
  if (diffSec < 86400) return Math.floor(diffSec / 3600) + "h ago";
  return Math.floor(diffSec / 86400) + "d ago";
}

function variantForJob(job) {
  if (job.state === "awaiting_2fa") return "2fa";
  if (ACTIVE_STATES.has(job.state)) return "running";
  if (job.state === "succeeded") return "success";
  if (job.state === "failed" || job.state === "timed_out") return "failed";
  return "cancelled";
}

function iconForJob(job) {
  switch (job.state) {
    case "awaiting_2fa": return <KeyOutlined />;
    case "succeeded":    return <CheckOutlined />;
    case "failed":
    case "timed_out":    return <CloseOutlined />;
    case "cancelled":    return <StopOutlined />;
    case "running":
    case "claimed":
    case "queued":
    default:             return <LoadingOutlined spin />;
  }
}

function statusLine(job) {
  const carrier = carrierName(job);
  switch (job.state) {
    case "queued":       return "Waiting in queue";
    case "claimed":      return "Starting up" + (carrier ? " · " + carrier : "");
    case "running":      return "Running" + (carrier ? " · " + carrier : "");
    case "awaiting_2fa": return "Waiting for 2FA code" + (carrier ? " · " + carrier : "");
    case "succeeded":    return "Completed" + (carrier ? " · " + carrier : "");
    case "failed":       return "Failed" + (carrier ? " · " + carrier : "");
    case "timed_out":    return "Timed out" + (carrier ? " · " + carrier : "");
    case "cancelled":    return "Cancelled" + (carrier ? " · " + carrier : "");
    default:             return job.state;
  }
}

function ScrapeJobCard({ job, anchorTime }) {
  const variant = variantForJob(job);
  const className = "scrape-modal-card scrape-modal-card-" + variant;
  return (
    <div className={className}>
      <div className="scrape-modal-card-icon">{iconForJob(job)}</div>
      <div>
        <div className="scrape-modal-card-title">{jobLabel(job)}</div>
        <div className="scrape-modal-card-meta">{statusLine(job)}</div>
        {job.error_message ? (
          <span className="scrape-modal-card-error">{job.error_message}</span>
        ) : null}
      </div>
      <div className="scrape-modal-card-time">{formatRelative(anchorTime)}</div>
    </div>
  );
}

function PatientScrapeJobsModalInner({ open, onClose, jobs, patientName }) {
  const active = jobs.filter(function (j) { return ACTIVE_STATES.has(j.state); });
  const recent = jobs
    .filter(function (j) { return TERMINAL_STATES.has(j.state); })
    .sort(function (a, b) {
      const ta = a.completed_at || a.updated_at || "";
      const tb = b.completed_at || b.updated_at || "";
      return tb.localeCompare(ta);
    })
    .slice(0, 10);

  const isEmpty = active.length === 0 && recent.length === 0;

  const titleNode = (
    <div className="scrape-modal-title">
      <span className="scrape-modal-title-main">Insurance fetches</span>
      {patientName ? <span className="scrape-modal-title-sub">for {patientName}</span> : null}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={titleNode}
      footer={null}
      width={520}
      className="scrape-modal"
      destroyOnClose
    >
      {isEmpty ? (
        <div className="scrape-modal-empty">
          <div className="scrape-modal-empty-icon">
            <CloudSyncOutlined />
          </div>
          <h4 className="scrape-modal-empty-title">No fetches yet</h4>
          <p className="scrape-modal-empty-body">
            When you run an AI insurance fetch for this patient, the live
            progress and the latest results will show up right here.
          </p>
        </div>
      ) : null}

      {active.length > 0 ? (
        <div className="scrape-modal-section">
          <div className="scrape-modal-section-header">
            In progress · {active.length}
          </div>
          {active.map(function (job) {
            return (
              <ScrapeJobCard
                key={job.job_id}
                job={job}
                anchorTime={job.created_at || job.updated_at}
              />
            );
          })}
        </div>
      ) : null}

      {recent.length > 0 ? (
        <div className="scrape-modal-section">
          <div className="scrape-modal-section-header">
            Recently finished
          </div>
          {recent.map(function (job) {
            return (
              <ScrapeJobCard
                key={job.job_id}
                job={job}
                anchorTime={job.completed_at || job.updated_at}
              />
            );
          })}
        </div>
      ) : null}
    </Modal>
  );
}

const mapStateToProps = (state, ownProps) => {
  const ids = (state.scrapeJobs && state.scrapeJobs.idsByPatient[ownProps.patientId]) || [];
  const jobs = ids
    .map(function (id) { return state.scrapeJobs.byId[id]; })
    .filter(Boolean);
  return { jobs };
};

export default connect(mapStateToProps)(PatientScrapeJobsModalInner);
